import type { FC } from "react";
import { useMemo, useState } from "react";

import styled from "styled-components";

import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { ChannelsOverviewBarChart } from "../../../ChannelsOverviewBarChart/ChannelsOverviewBarChart";
import {
    dataModeMap,
    percentDataFormat,
} from "../../../ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { LegendContainer } from "../../../ChannelsOverviewBarChart/components/view-by-domain-chart/StyledComponents";
import { getInitialLegends } from "../../../ChannelsOverviewBarChart/components/view-by-domain-chart/utils";
import { TrafficSourceDisplayMap } from "../../../ChannelsOverviewBarChart/types";
import ChartWrapperWithKey from "../../../ChannelsOverviewBarChart/utils/ChartWrapperWithKey";

import { getChartConfig } from "./getChartConfig";
import type { IDomainPopData, IDomainsChangeValues, IViewByDomainPopChart } from "./types";
import { calculateChangeValuesPerDomain, getLegends, processDataDomainToShow } from "./utils";

const ViewByDomainPopChartWrapper = styled(FlexRow)`
    box-sizing: border-box;
    margin-top: 32px;
    min-height: 313px;
`;

const ViewByDomainPopChart: FC<IViewByDomainPopChart> = ({
    chartRawData,
    durations,
    dataFormat,
    trafficSourcesMap = TrafficSourceDisplayMap,
}) => {
    const isPercentageDataMode = dataModeMap[dataFormat] === dataModeMap[percentDataFormat];

    const initialSeries = useMemo(
        () => processDataDomainToShow(chartRawData, isPercentageDataMode, trafficSourcesMap),
        [chartRawData, isPercentageDataMode, trafficSourcesMap],
    );
    const initialLegends = useMemo(() => getInitialLegends(initialSeries, trafficSourcesMap), [initialSeries, trafficSourcesMap]);
    const [legends, setLegends] = useState<ILegendItems>(() => initialLegends);

    const visibleLegends = legends.filter((legend) => legend.visible).map((legend) => legend.id);

    const filteredChartRawData = useMemo(() => {
        return Object.keys(chartRawData || {}).reduce((acc, key) => {
            if (visibleLegends.includes(key)) {
                acc[key] = chartRawData[key];
            }
            return acc;
        }, {} as IDomainPopData);
    }, [chartRawData, visibleLegends]);

    const series = useMemo(() => {
        return processDataDomainToShow(filteredChartRawData, isPercentageDataMode, trafficSourcesMap);
    }, [filteredChartRawData, isPercentageDataMode, trafficSourcesMap]);

    const domainsChangeValues: IDomainsChangeValues = useMemo(
        () => calculateChangeValuesPerDomain(series, isPercentageDataMode),
        [series, isPercentageDataMode],
    );

    const config = useMemo(
        () => getChartConfig(series, isPercentageDataMode, durations, domainsChangeValues),
        [series, isPercentageDataMode, durations, domainsChangeValues],
    );

    const onLegendClick = (filter): void => {
        setLegends((prevLegends) =>
            prevLegends.map((legend) =>
                legend.name === filter.name ? { ...legend, visible: !legend.visible } : legend,
            ),
        );
    };

    return (
        <ViewByDomainPopChartWrapper data-automation="ViewByDomainPopChart">
            <ChartWrapperWithKey dependency={series}>
                <ChannelsOverviewBarChart
                    data={series}
                    config={config}
                    dataAutomation="ChannelsOverviewBarChart"
                />
            </ChartWrapperWithKey>
            <LegendContainer>
                <FlexRow>{getLegends(legends, onLegendClick)}</FlexRow>
            </LegendContainer>
        </ViewByDomainPopChartWrapper>
    );
};

export default ViewByDomainPopChart;
